import {
  faChevronLeft,
  faChevronRight,
  faExpand,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DynamicModal from "components/modals/DynamicModal";
import React, { useEffect, useRef, useState } from "react";
import {
  Col,
  Row,
  Form,
  Button,
  Placeholder,
  Modal,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FaCheck, FaCheckCircle } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  forceSubmitAssignment,
  getSubmission,
  saveProgress,
  submitAssignment,
  terminatedAssignment,
} from "services/Student/Submission.services";
import { formatText } from "utils/FormatText";
import useAutoSave from "hooks/useAutoSave";
import AutoSaveIndicator from "components/shared/AutoSaveIndicator";

const countWords = (str) => {
  if (!str) return 0;
  return str.trim().split(/\s+/).filter(Boolean).length;
};

const AttemptExercise = ({ padding }) => {
  const { t } = useTranslation();
  const [exerciseInfo, setExerciseInfo] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [writtenResponses, setWrittenResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [workRestored, setWorkRestored] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fullView, setFullView] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [assignmentDetails, setAssignmentDetails] = useState(null);
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [focusWarnings, setFocusWarnings] = useState(0);
  const [showFocusWarning, setShowFocusWarning] = useState(false);
  const [focusWarningReason, setFocusWarningReason] = useState("tab");
  const [showSubmitWarningModal, setShowSubmitWarningModal] = useState(false);
  const focusWarningsRef = useRef(0);
  const MAX_FOCUS_WARNINGS = 3;
  const dangerousKeyRef = useRef(false);
  const dangerousKeyTimerRef = useRef(null);
  // const [showWarningModal, setShowWarningModal] = useState(false);
  // const [showWarning, setShowWarning] = useState(false);

  const navigate = useNavigate();
  const params = useParams();
  const id = params._id;
  const [showFullScreenModal, setShowFullScreenModal] = useState(false);

  const enableFullScreen = () => {
    const element = document.documentElement;
    // requestFullscreen() returns a promise that REJECTS when the browser
    // blocks it — e.g. when it isn't triggered by a user gesture (page refresh)
    // or in Incognito. Swallow that rejection so it doesn't surface as an
    // uncaught "Permissions check failed" runtime error; the fullscreen-warning
    // modal already prompts the student to click to re-enter fullscreen.
    try {
      let result;
      if (element.requestFullscreen) {
        result = element.requestFullscreen();
      } else if (element.mozRequestFullScreen) {
        result = element.mozRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        result = element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        result = element.msRequestFullscreen();
      }
      if (result && typeof result.catch === "function") {
        result.catch(() => {});
      }
    } catch (e) {
      /* fullscreen unavailable / blocked — handled by the warning modal */
    }
  };

  const checkFullScreen = () => {
    if (!document.fullscreenElement) {
      focusWarningsRef.current += 1;
      setFocusWarnings(focusWarningsRef.current);
      if (focusWarningsRef.current >= MAX_FOCUS_WARNINGS) {
        handleTerminated();
      } else {
        setFocusWarningReason("fullscreen");
        setShowFocusWarning(true);
        setShowFullScreenModal(true);
      }
    }
  };

  const handleFullScreenReEntry = () => {
    enableFullScreen();
    setShowFullScreenModal(false);
  };

  useEffect(() => {
    const fetchExerciseDetails = async () => {
      try {
        setLoading(true);
        const response = await getSubmission(id);
        const { exercise, answers, _id, endTime } = response?.data?.result;
        setAssignmentDetails(response?.data?.result?.assignment)
        console.log("response", response.data.result.assignment);

        if (endTime) {
          const endTimeMs = new Date(endTime).getTime();
          const now = new Date().getTime();
          const timeDiff = endTimeMs - now;

          if (timeDiff > 0) {
            setTimeRemaining(timeDiff);
          } else {
            setShowTimeUpModal(true);
          }
        } else {
          // No time limit, so we set `timeRemaining` to null
          setTimeRemaining(null);
        }

        const prefilledAnswers = {};
        const prefilledWrittenResponses = {};

        answers.forEach((ans) => {
          const question = exercise.questions.find(
            (q) => q._id === ans.question
          );
          if (question) {
            if (
              question.type === "multiple-choice" ||
              question.type === "true-false"
            ) {
              prefilledAnswers[ans.question] = ans.answer;
            } else if (
              question.type === "fill-in-the-blank" ||
              question.type === "written-answer"
            ) {
              prefilledWrittenResponses[ans.question] = ans.answer;
            }
          }
        });

        // Seed the autosave baseline with the server values so an untouched
        // form is never treated as dirty and the first save sends only real
        // changes.
        markSaved({
          selectedAnswers: prefilledAnswers,
          writtenResponses: prefilledWrittenResponses,
        });

        // Restore a locally-stashed draft if it is newer than the server data.
        let restoredAnswers = prefilledAnswers;
        let restoredWritten = prefilledWrittenResponses;
        try {
          const storageKey = `submission-progress-${_id}`;
          const raw = localStorage.getItem(storageKey);
          if (raw) {
            const draft = JSON.parse(raw);
            const serverTs = new Date(
              response?.data?.result?.updatedAt || 0
            ).getTime();
            if (draft?.data && (draft.ts || 0) > serverTs) {
              if (draft.data.selectedAnswers)
                restoredAnswers = draft.data.selectedAnswers;
              if (draft.data.writtenResponses)
                restoredWritten = draft.data.writtenResponses;
              setWorkRestored(true);
              setTimeout(() => setWorkRestored(false), 6000);
            }
          }
        } catch (e) {
          /* ignore malformed draft */
        }

        setSelectedAnswers(restoredAnswers);
        setWrittenResponses(restoredWritten);
        setExerciseInfo(exercise);
        setSubmissionId(_id);
        setAudioUrl(exercise?.audioUrl?.url);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching Exercise details:", error);
      }
    };

    fetchExerciseDetails();
  }, [id]);

  useEffect(() => {
    if (timeRemaining && timeRemaining > 0) {
      const intervalId = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1000) {
            clearInterval(intervalId);
            forceSubmitAssignment(submissionId)
              .then(() => {
                toast.success(t("Time's up! Assignment has been submitted."));
                setShowTimeUpModal(true);
              })
              .catch((error) => {
                toast.error(t("Error submitting assignment after timeout."));
                console.error(error);
              });
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [timeRemaining]);

  useEffect(() => {
    let timeoutId;

    const enableFullScreen = () => {
      const element = document.documentElement;
      // Swallow the rejection browsers throw when fullscreen isn't allowed
      // (no user gesture on refresh / Incognito) — otherwise it surfaces as an
      // uncaught "Permissions check failed" runtime error.
      try {
        let result;
        if (element.requestFullscreen) {
          result = element.requestFullscreen();
        } else if (element.mozRequestFullScreen) {
          result = element.mozRequestFullScreen();
        } else if (element.webkitRequestFullscreen) {
          result = element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          result = element.msRequestFullscreen();
        }
        if (result && typeof result.catch === "function") {
          result.catch(() => {});
        }
      } catch (e) {
        /* fullscreen blocked — handled by the warning modal */
      }
    };

    const handleInstantTermination = () => {
      dangerousKeyRef.current = false;
      if (dangerousKeyTimerRef.current) clearTimeout(dangerousKeyTimerRef.current);
      handleTerminated();
    };

    const handleFocusLoss = () => {
      if (dangerousKeyRef.current) {
        // Student used Alt/Win key to switch — no warnings, instant termination
        handleInstantTermination();
        return;
      }
      focusWarningsRef.current += 1;
      setFocusWarnings(focusWarningsRef.current);
      if (focusWarningsRef.current >= MAX_FOCUS_WARNINGS) {
        handleTerminated();
      } else {
        setFocusWarningReason("tab");
        setShowFocusWarning(true);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        if (dangerousKeyRef.current) {
          handleInstantTermination();
        } else {
          timeoutId = setTimeout(handleFocusLoss, 3000);
        }
      } else if (document.visibilityState === "visible") {
        clearTimeout(timeoutId);
        dangerousKeyRef.current = false;
        if (dangerousKeyTimerRef.current) clearTimeout(dangerousKeyTimerRef.current);
      }
    };

    const handleBlur = () => {
      if (dangerousKeyRef.current) {
        handleInstantTermination();
      } else {
        timeoutId = setTimeout(handleFocusLoss, 3000);
      }
    };

    const handleFocus = () => {
      clearTimeout(timeoutId);
      dangerousKeyRef.current = false;
      if (dangerousKeyTimerRef.current) clearTimeout(dangerousKeyTimerRef.current);
    };

    const handleDangerousKeyDown = (e) => {
      const isAlt = e.key === "Alt";
      const isMeta = e.key === "Meta" || e.key === "OS";
      const isCtrlTab = e.ctrlKey && e.key === "Tab";
      const isAltF4 = e.altKey && e.key === "F4";

      if (isCtrlTab || isAltF4) {
        e.preventDefault();
        handleInstantTermination();
        return;
      }

      if (isAlt || isMeta) {
        dangerousKeyRef.current = true;
        if (dangerousKeyTimerRef.current) clearTimeout(dangerousKeyTimerRef.current);
        // Auto-clear flag after 2s if focus was never lost (key released harmlessly)
        dangerousKeyTimerRef.current = setTimeout(() => {
          dangerousKeyRef.current = false;
        }, 2000);
      }
    };

    const handleDangerousKeyUp = (e) => {
      if (e.key === "Alt" || e.key === "Meta" || e.key === "OS") {
        if (document.hasFocus()) {
          // Released Alt/Win without switching window — clear the flag
          dangerousKeyRef.current = false;
          if (dangerousKeyTimerRef.current) clearTimeout(dangerousKeyTimerRef.current);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("fullscreenchange", checkFullScreen);
    document.addEventListener("keydown", handleDangerousKeyDown);
    document.addEventListener("keyup", handleDangerousKeyUp);

    enableFullScreen();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("fullscreenchange", checkFullScreen);
      document.removeEventListener("keydown", handleDangerousKeyDown);
      document.removeEventListener("keyup", handleDangerousKeyUp);
    };
  }, []);
  useEffect(() => {
    const disableRightClick = (e) => {
      e.preventDefault();
    };

    const disableCopyPaste = (e) => {
      e.preventDefault();
    };

    const detectPrintScreen = (e) => {
      if (e.key === "PrintScreen") {
        e.preventDefault();
        navigator.clipboard.writeText("");
      }
    };

    const blockDeveloperTools = (e) => {
      if (
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J" || e.key === "C")) ||
        (e.ctrlKey && e.key === "U") ||
        e.key === "F12"
      ) {
        e.preventDefault();
        // alert(t("Developer tools are disabled during the quiz."));
      }
    };

    document.addEventListener("contextmenu", disableRightClick);
    document.addEventListener("copy", disableCopyPaste);
    document.addEventListener("cut", disableCopyPaste);
    document.addEventListener("paste", disableCopyPaste);
    document.addEventListener("keydown", detectPrintScreen);
    document.addEventListener("keydown", blockDeveloperTools);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
      document.removeEventListener("copy", disableCopyPaste);
      document.removeEventListener("cut", disableCopyPaste);
      document.removeEventListener("paste", disableCopyPaste);
      document.removeEventListener("keydown", detectPrintScreen);
      document.removeEventListener("keydown", blockDeveloperTools);
    };
  }, []);

  const handleOptionChange = (questionId, selectedOption) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [questionId]: selectedOption,
    });
  };

  const handleTextAreaChange = (questionId, value) => {
    setWrittenResponses({
      ...writtenResponses,
      [questionId]: value,
    });
    console.log("written re", writtenResponses);
  };

  // Build a save payload from only the changed answer fields. The autosave hook
  // diffs `selectedAnswers` / `writtenResponses` and passes only the ones that
  // actually changed, so we transmit just those answers while preserving the
  // required submissionId identifier.
  const buildChangedPayload = (changed) => {
    const changedAnswers = {
      ...(changed.selectedAnswers || {}),
      ...(changed.writtenResponses || {}),
    };
    return {
      submissionId,
      answers: Object.keys(changedAnswers).map((questionId) => ({
        questionId,
        answer: changedAnswers[questionId],
      })),
    };
  };

  // Debounced auto-save: fires ~2.5s after the student stops answering, and at
  // least every 30s while they keep answering. Also flushes on tab-hide / blur /
  // page-leave / network-restore, and stashes work locally when offline.
  const {
    status: autoSaveStatus,
    lastSavedAt: autoSaveAt,
    isOnline: autoSaveOnline,
    markSaved,
  } = useAutoSave(
    { selectedAnswers, writtenResponses },
    (changed, { full }) => saveProgress(buildChangedPayload(changed)),
    {
      enabled:
        Boolean(submissionId) &&
        !showModal &&
        !showTimeUpModal &&
        !showFullScreenModal,
      saveOnLifecycle: true,
      storageKey: submissionId ? `submission-progress-${submissionId}` : undefined,
    }
  );

  const handleTerminated = async () => {
    try {
      await terminatedAssignment(id);
      setShowModal(true);
      toast.info(t("Assignment is Terminated!"));
    } catch (error) {
      toast.error(t("Error Terminated Assignment"));
      console.error("Error Terminated Assignment:", error);
    }
  }
  const handleNavigate = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        navigate("/assignments"); // Navigate after exiting fullscreen
      }).catch((err) => {
        console.error("Failed to exit fullscreen mode:", err);
        navigate("/assignments"); // Fallback: Navigate even if exiting fullscreen fails
      });
    } else {
      navigate("/assignments"); // Navigate directly if not in fullscreen mode
    }
  };

  const doSubmit = async () => {
    setSaving(true);
    const combinedAnswers = { ...selectedAnswers, ...writtenResponses };
    const payload = {
      submissionId,
      answers: Object.keys(combinedAnswers).map((questionId) => ({
        questionId,
        answer: combinedAnswers[questionId],
      })),
    };
    try {
      await submitAssignment(payload);
      toast.success(t("Assignment submitted successfully!"));
      if (document.fullscreenElement) {
        document.exitFullscreen()
          .then(() => navigate("/assignments"))
          .catch(() => navigate("/assignments"));
      } else {
        navigate("/assignments");
      }
      setSaving(false);
    } catch (error) {
      toast.error(t("Error submitting assignment, please try again."));
      console.error("Error submitting assignment:", error);
      setSaving(false);
    }
  };

  const handleSubmitAssignment = () => {
    const combinedAnswers = { ...selectedAnswers, ...writtenResponses };

    // Whitespace-only counts as unanswered — otherwise a student can submit a
    // blank assignment by typing a space.
    const isBlank = (questionId) =>
      String(combinedAnswers[questionId] ?? "").trim().length === 0;

    const requiredUnanswered = exerciseInfo?.questions
      .filter((q) => !q.optional)
      .some((q) => isBlank(q._id));

    if (requiredUnanswered) {
      toast.error(t("Please answer all required questions before submitting."));
      return;
    }

    const optionalUnanswered = exerciseInfo?.questions
      .filter((q) => q.optional)
      .some((q) => isBlank(q._id));

    if (optionalUnanswered) {
      setShowSubmitWarningModal(true);
      return;
    }

    doSubmit();
  };

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  };

  const handleView = () => {
    setFullView(!fullView);
  };

  const isWritingExercise = exerciseInfo?.exerciseType === "writing";
  const isReadingExercise = exerciseInfo?.exerciseType === "reading";
  const isListeningExercise = exerciseInfo?.exerciseType === "listening";


  const stripHtml = (html) => html.replace(/<\/?[^>]+(>|$)/g, "");

  const highlightPassage = (passage, words) => {
    if (!passage || !words) return passage;

    const cleanPassage = stripHtml(passage);
    const wordList = words.map((word) => word.title);
    const regex = new RegExp(`\\b(${wordList.join("|")})\\b`, "gi");

    return cleanPassage.split(regex).map((part, index) => {
      const matchedWord = words.find(
        (word) => word.title.toLowerCase() === part.toLowerCase()
      );

      if (matchedWord) {
        return (
          <span
            key={index}
            className="highlighted-word"
            title={`Meaning: ${matchedWord.meaning.join(", ")} | Sentence: ${matchedWord.sentence}`}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // Function to render tooltips
  const renderTooltip = (word) => (
    <Tooltip id={`tooltip-${word._id}`}>
      <strong>{word.title}</strong>
      <div>Meaning: {word.meaning.join(", ")}</div>
      <div>Sentence: {word.sentence}</div>
    </Tooltip>
  );

  return (
    <div className={`${padding ? 'p-4' : ''}`}>
      <div>
        {exerciseInfo ? (

          <div className="d-flex justify-content-between">

            <div>
              <h3>{exerciseInfo.title}</h3>
            </div>
            {timeRemaining !== null ? (
              <div className="timer mt-2 me-2">
                {t("Time remaining:")}{" "}
                <span className="text-danger">{formatTime(timeRemaining)}</span>
              </div>
            ) : (
              <div className="timer mt-2 me-2">
                <span className="text-success"></span>
              </div>
            )}
          </div>
        ) : (
          <Placeholder as="div" animation="glow">
            <Placeholder xs={3} className="mb-2" />
            <div>
              <Placeholder xs={6} />
            </div>
          </Placeholder>
        )}
      </div>

      <div
        style={{
          height: "75vh",
          border: "1px solid #A7A7A7",
          borderRadius: "30px",
          padding: "30px",
        }}
      >
        <Row className="h-100">
          {isReadingExercise && (
            <Col
              xs={fullView ? 12 : 6}
              style={{
                borderRight: fullView ? "none" : "2px solid #8A8A8A",
                height: "70vh",
              }}
            >
              {exerciseInfo ? (
                <>
                  <div className="d-flex justify-content-between">
                    <div>
                      <h3 className="text-capitalize">{t("Comprehension Passage")}</h3>
                    </div>
                    <div>
                      <Button
                        onClick={() => handleView()}
                        className="button-style border-0"
                      >
                        <FontAwesomeIcon
                          icon={fullView ? faChevronLeft : faChevronRight}
                        />
                        {fullView && (
                          <span className="ms-2">{t("Show Questions")}</span>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div
                    className="custom-scrollbar"
                    // style={{
                    //   height: "90%",
                    //   overflow: "auto",
                    // }}
                    style={{
                      height: "90%",
                      overflow: "auto",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {/* <div>
                      {exerciseInfo?.passage
                        ?.split('\n\n')
                        .map((paragraph, index) => (
                          <p key={index}>
                            {highlightPassage(paragraph, exerciseInfo?.words)}
                          </p>
                        ))}

                    </div> */}
                    <p>{formatText(exerciseInfo?.passage)}</p>

                  </div>
                </>
              ) : (
                <Placeholder as="div" animation="glow">
                  <Placeholder xs={6} className="mb-2" />
                  <Placeholder xs={8} />
                  <Placeholder xs={4} />
                  <Placeholder xs={7} />
                </Placeholder>
              )}
            </Col>
          )}

          {isListeningExercise && (
            <Col
              xs={6}
              className="custom-scrollbar"
              style={{
                height: "70vh",
                overflow: "auto",
                borderRight: "2px solid #8A8A8A",
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-3">
                {/* <h4>{exerciseInfo.title}</h4>
                  <span className={`bg-primary px-3 mx-2 rounded-4 text-white`}>
                    {exerciseInfo.status}
                  </span> */}
                <h5>
                  {t(`Listen carefully to the audio provided. After listening,
                  please answer the following questions based on the content of
                  the audio.`)}
                </h5>
              </div>
              <div className="">
                <audio controls src={audioUrl} className="mt-5 w-100" />
              </div>
            </Col>
          )}

          <Col
            xs={isWritingExercise ? 12 : fullView ? 0 : 6}
            className={`${fullView ? "d-none" : "d-block"} custom-scrollbar`}
            style={{ height: "70vh", overflow: "auto" }}
          >
            {exerciseInfo?.instructions && (
              <div
                className="mb-3 p-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "10px",
                  border: "1px solid #dee2e6",
                }}
              >
                <h6 className="fw-bold mb-1">{t("Instructions")}</h6>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {exerciseInfo.instructions}
                </p>
              </div>
            )}
            {exerciseInfo ? (
              exerciseInfo.questions.map((question, index) => (
                <div key={question._id} style={{ marginBottom: "20px" }}>
                  <div className="d-flex align-items-start flex-wrap gap-1">
                    <h5 className="pe-2 mb-0">
                      {t("Question")} {index + 1}:
                    </h5>
                    <h5 style={{ whiteSpace: "pre-wrap" }} className="mb-0">
                      {question.questionText} (
                      {question.score} {t("points")})
                    </h5>
                    {question.optional && (
                      <span
                        className="badge bg-secondary align-self-center"
                        style={{ fontSize: "11px" }}
                      >
                        {t("Optional")}
                      </span>
                    )}
                  </div>

                  {question.type === "multiple-choice" && (
                    <Form>
                      {question.options.map((option, i) => (
                        <Form.Check
                          type="radio"
                          label={option}
                          name={`question-${question._id}`}
                          key={i}
                          value={option}
                          checked={selectedAnswers[question._id] === option}
                          onChange={() =>
                            handleOptionChange(question._id, option)
                          }
                        />
                      ))}
                    </Form>
                  )}

                  {question.type === "true-false" && (
                    <Form>
                      <Form.Check
                        type="radio"
                        label="True"
                        name={`question-${question._id}`}
                        value="True"
                        checked={selectedAnswers[question._id] === "True"}
                        onChange={() =>
                          handleOptionChange(question._id, "True")
                        }
                      />
                      <Form.Check
                        type="radio"
                        label="False"
                        name={`question-${question._id}`}
                        value="False"
                        checked={selectedAnswers[question._id] === "False"}
                        onChange={() =>
                          handleOptionChange(question._id, "False")
                        }
                      />
                    </Form>
                  )}

                  {["written-answer", "fill-in-the-blank"].includes(
                    question.type
                  ) && (
                      <Form.Group>
                        <div style={{ position: "relative" }}>
                          <Form.Control
                            as="textarea"
                            rows={10}
                            spellCheck={false}
                            autoCorrect="off"
                            autoCapitalize="off"
                            value={writtenResponses[question._id] || ""}
                            onChange={(e) =>
                              handleTextAreaChange(question._id, e.target.value)
                            }
                            placeholder={t("Write your answer here...")}
                          />
                          <Button
                            size="sm"
                            variant="light"
                            onClick={() => setExpandedQuestionId(question._id)}
                            title={t("Expand")}
                            aria-label={t("Expand")}
                            style={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              border: "1px solid #dee2e6",
                              padding: "2px 8px",
                              lineHeight: 1,
                            }}
                          >
                            <FontAwesomeIcon icon={faExpand} />
                          </Button>
                        </div>
                        <div className="d-flex justify-content-end text-muted small mt-1">
                          {countWords(writtenResponses[question._id])} {t("words")}
                        </div>
                      </Form.Group>
                    )}
                </div>
              ))
            ) : (
              <Placeholder as="div" animation="glow">
                <Placeholder xs={8} className="mb-2" />
                <Placeholder xs={12} />
                <Placeholder xs={10} />
              </Placeholder>
            )}
          </Col>
        </Row>
      </div>

      <div className="mt-2 d-flex justify-content-end">
        <div className="d-flex gap-3" style={{ alignItems: "center" }}>
          {workRestored && (
            <span className="text-success small">
              {t("Your work has been restored.")}
            </span>
          )}
          <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={autoSaveAt} isOnline={autoSaveOnline} />
          <Button
            variant="primary"
            className="mt-1 px-4 py-2 border-0 button-style"
            onClick={handleSubmitAssignment}
            disabled={
              exerciseInfo?.questions
                .filter((q) => !q.optional)
                // Whitespace-only is not an answer, so keep this in step with
                // the check in handleSubmitAssignment — otherwise the button
                // looks enabled and the click is then rejected.
                .some(
                  (q) =>
                    String(
                      selectedAnswers[q._id] ?? writtenResponses[q._id] ?? ""
                    ).trim().length === 0
                ) || saving
            }
          >
            {t("Submit Assignment")}
          </Button>
        </div>
      </div>

      {/* <Modal show={showTimeUpModal} onHide={() => navigate("/assignments")}>
        <Modal.Header closeButton>
          <Modal.Title>Time's Up!</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your time for this exercise has expired.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => navigate("/assignments")}>
            Back to Assignments
          </Button>
        </Modal.Footer>
      </Modal> */}
      <DynamicModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        handleConfirm={handleNavigate}
        heading="Assignment Terminated"
        description="Your assigment has been terminated due to suspicious activity (e.g., switching tabs)."
        confirmText="Back to Main Menu"
        denyText="Cancel"
        cancelBtn={false}
      />
      <DynamicModal
        show={showFullScreenModal}
        handleConfirm={() => { handleFullScreenReEntry(); setShowFocusWarning(false); }}
        handleExtra={() => { setShowFullScreenModal(false); setShowFocusWarning(false); handleNavigate(); }}
        heading={focusWarnings >= MAX_FOCUS_WARNINGS - 1 ? "Final Warning" : "Warning"}
        description={
          focusWarnings >= MAX_FOCUS_WARNINGS - 1
            ? `You exited fullscreen. This is your final warning (${focusWarnings} of ${MAX_FOCUS_WARNINGS}). One more and the assignment will be terminated. You must return to fullscreen now.`
            : `You exited fullscreen. Warning ${focusWarnings} of ${MAX_FOCUS_WARNINGS}. You must stay in fullscreen during the assignment.`
        }
        confirmText="Return to Fullscreen"
        denyText=""
        extraText="Exit Anyway"
        cancelBtn={false}
        extraBtn={true}
      />

      <DynamicModal
        show={showFocusWarning && !showFullScreenModal}
        handleClose={() => setShowFocusWarning(false)}
        handleConfirm={() => setShowFocusWarning(false)}
        handleExtra={() => { setShowFocusWarning(false); handleNavigate(); }}
        heading={
          focusWarnings >= MAX_FOCUS_WARNINGS - 1 ? "Final Warning" : "Warning"
        }
        description={
          focusWarningReason === "fullscreen"
            ? focusWarnings >= MAX_FOCUS_WARNINGS - 1
              ? `You exited fullscreen. This is your final warning (${focusWarnings} of ${MAX_FOCUS_WARNINGS}). One more and the assignment will be terminated.`
              : `You exited fullscreen. Warning ${focusWarnings} of ${MAX_FOCUS_WARNINGS}. You must stay in fullscreen — reaching ${MAX_FOCUS_WARNINGS} warnings will terminate the assignment.`
            : focusWarnings >= MAX_FOCUS_WARNINGS - 1
              ? `You left the assignment window. This is your final warning (${focusWarnings} of ${MAX_FOCUS_WARNINGS}). One more and the assignment will be terminated.`
              : `You left the assignment window. Warning ${focusWarnings} of ${MAX_FOCUS_WARNINGS}. Stay on this tab — reaching ${MAX_FOCUS_WARNINGS} warnings will terminate the assignment.`
        }
        confirmText="I understand"
        extraText="Exit Anyway"
        cancelBtn={false}
        extraBtn={true}
      />

      <DynamicModal
        show={showSubmitWarningModal}
        handleClose={() => setShowSubmitWarningModal(false)}
        handleConfirm={() => { setShowSubmitWarningModal(false); doSubmit(); }}
        heading={t("Unanswered Questions")}
        description={t(
          "You have not answered all questions. Optional questions have been left blank. Are you sure you want to submit?"
        )}
        confirmText={t("Yes, Submit")}
        denyText={t("Go Back")}
        cancelBtn={true}
      />

      <Modal
        show={!!expandedQuestionId}
        onHide={() => setExpandedQuestionId(null)}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>
            {exerciseInfo?.questions.find(
              (q) => q._id === expandedQuestionId
            )?.questionText}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            autoFocus
            spellCheck={false}
            autoCorrect="off"
            autoCapitalize="off"
            value={
              expandedQuestionId
                ? writtenResponses[expandedQuestionId] || ""
                : ""
            }
            onChange={(e) =>
              expandedQuestionId &&
              handleTextAreaChange(expandedQuestionId, e.target.value)
            }
            placeholder={t("Write your answer here...")}
            style={{ minHeight: "60vh", resize: "vertical" }}
          />
          <div className="d-flex justify-content-between text-muted small mt-2">
            <span>
              {countWords(
                expandedQuestionId ? writtenResponses[expandedQuestionId] : ""
              )}{" "}
              {t("words")}
            </span>
            <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={autoSaveAt} isOnline={autoSaveOnline} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setExpandedQuestionId(null)}
          >
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AttemptExercise;
